<template>
  <div class="frontend-root">
    <SiteNav :advisor-data="advisor" />
    <router-view
      :advisor="advisor"
      :features="features"
    />
    <DashboardMessages />
  </div>
</template>

<script>
import * as Sentry from '@sentry/vue';
import SiteNav from '@frontend/ui/features/nav/SiteNav.vue';
import { apiFetch } from '../api/api';
import { useAdvisorStore } from '@frontend/ui/stores/advisorStore.js';
import DashboardMessages from '@frontend/dash3/js/components/DashboardMessages.vue';
import { mapStores } from 'pinia';
import { Button, TextLink } from '@stakeui';


export default {
  name: 'FrontendRoot',
  components: {
    SiteNav,
    Button,
    TextLink,
    DashboardMessages,
  },
  data() {
    return {
      advisor: null,
    };
  },
  computed: {
    ...mapStores(useAdvisorStore),
    /*
    Creates a new property
    Updates when any dependant property changes */
    features() {
      const flags = this.advisor?.user?.feature_flags;
      if (!flags?.length) return {};

      const names = flags?.map(f => f.replace(/[Ff]eature:?\s+/, '').trim());
      // Return a hash of enabled feature names
      return names?.reduce((hash, name) => ({ ...hash, [name]: true }), {});
    },
    isEmbed() {
      const path = this.$router?.currentRoute?.path;
      return !!path ? path.startsWith('/embed/') : false;
    },
    headers() {
      return {
        'X-CSRFToken': this.$root.$data.csrfToken,
        'Content-Type': 'application/json',
      };
    },
  },
  watch: {
  },
  mounted() {
    this.advisorStore.setCSRF(this.$root.$data.csrfToken);
    this.getAdvisorData();
    try {
      this.getActiveOAuthConnections();
    } catch (e) {
      console.error('Error getting active OAuth connections', e);
    }
    this.manageEmbed();
  },
  methods: {
    hasFeature(name) {
      return !!this.features?.[name];
    },
    async getAdvisorData() {
      const res = await (await apiFetch('/api/v1/advisor-info', { method: 'GET' })).json();
      const advisor = res.advisor;

      // Link user to Sentry
      const user = advisor?.user;
      if (user) {
        Sentry.setUser({
          id: user.id,
          email: user.email,
        });
      }

      // Save advisor data to store
      this.advisor = advisor;
      this.advisorStore.setAdvisor(advisor);

      // Make sure we are logged in before trying to get logo and settings
      if (advisor?.id && !advisor?.is_visitor) {
        this.getFirmAdvisorCoreSettings();
        this.getFirmLogo();
      }
    },
    async getActiveOAuthConnections() {
      const res = await fetch('/api/v1/oauth/connections/', {
        method: 'GET',
        headers: this.headers,
      });
      const data = await res.json();

      if (Array.isArray(data)) {
        this.advisorStore.activeOAuthConnections = data.reduce((acc, app) => {
          acc[app] = true;
          return acc;
        }, {});
        //
      } else {
        this.advisorStore.activeOAuthConnections = {};
      }
    },
    async getFirmAdvisorCoreSettings() {
      const url = `/api/v1/portfolio_checkup/get-or-create-settings/`;
      const res = await fetch(url, {
        method: 'GET',
        headers: this.headers,
      });
      const data = await res.json();
      console.log(data);
      const settings = data.settings;
      const canEditFirmSettings = data.can_edit_firm_settings;
      const pageSettings = data.pageSettings; // New line
      console.log('Firm Advisor Core Settings', settings);
      console.log('Can Edit Firm Settings', canEditFirmSettings);
      console.log('Page Settings', pageSettings); // New line
      this.advisorStore.setFirmAdvisorCoreSettings(settings);
      this.advisorStore.setCanEditFirmSettings(canEditFirmSettings);
      this.advisorStore.setPageSettings(pageSettings);
      this.advisorStore.setAdvisorHoldingsFormat(settings?.holdings_column_format || null);
      this.advisorStore.setPortfolioFeeDefaults(settings?.portfolio_fee_defaults || null);
      this.advisorStore.setHoldingsExportRules(settings?.holdings_export_rules || []);
    },
    async getFirmLogo() {
      const res = await (await apiFetch('/api/v1/firm-logo-base64', { method: 'GET' })).json();
      console.log('Firm Logo', res);
      if (res.base64) this.advisorStore.setFirmLogoBase64(res.base64);
    },
    manageEmbed() {
      if (this.$router.currentRoute.path.startsWith('/embed/')) {
        this.$root.$data.isEmbed = true;
        window.addEventListener('message', this.handleMessage);
      }
    },
    async fetchOAuthToken({ clientId, authCode, pkceCodeVerifier, oauthRedirectUri }) {
      const response = await apiFetch('/oauth/token/',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams({
              'grant_type': 'authorization_code',
              'client_id': clientId,
              'code': authCode,
              'code_verifier': pkceCodeVerifier,
              'redirect_uri': oauthRedirectUri,
            }),
          });
      // TODO handle error response, and also capture refresh token
      return (await response.json())['access_token'];
    },
    async fetchSesameToken() {
      const response = await apiFetch('/sesame/token/', { method: 'POST' });
      return (await response.json())['sesame_token'];
    },
    async handleMessage(event) {
      // Listen for message from client app with access token
      const { messageType, messageData } = event.data;
      if (messageType === 'auth_data') {
        const oauthToken = await this.fetchOAuthToken(messageData);
        localStorage.setItem('oauthToken', oauthToken);
        const sesameToken = await this.fetchSesameToken();
        localStorage.setItem('sesameToken', sesameToken);
        if (!this.advisor) this.getAdvisorData();
      }
    },
  },
};
</script>

<style scoped>


</style>


<style>
  .is-absolute { position: absolute; }

  .is-flex-col {
    display: flex;
    flex-direction: column;
  }
  .is-flex-center {
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .is-flex-col-center {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }

  /* Justify Content on Main Axis */
  .justify-start   { justify-content: flex-start; }
  .justify-end     { justify-content: flex-end; }
  .justify-center  { justify-content: center; }
  .justify-between { justify-content: space-between; }
  .justify-around  { justify-content: space-around; }
  .justify-even    { justify-content: space-evenly; }

  /* Align Items on Cross Axis */
  .align-start     { align-items: flex-start; }
  .align-end       { align-items: flex-end; }
  .align-center    { align-items: center; }
  .align-stretch   { align-items: stretch; }
  .align-c-start   { align-content: flex-start; }
  .align-c-end     { align-content: flex-end; }
  .align-c-center  { align-content: center; }
  .align-c-stretch { align-content: stretch; }

  /* Flex Gap between Items */
  .gap-1-4 { gap: 0.25em; }
  .gap-1-2 { gap: 0.5em; }
  .gap-3-4 { gap: 0.75em; }
  .gap-1   { gap: 1rem; }
  .gap-2   { gap: 2rem; }
  .gap-3   { gap: 3rem; }

  /* Flex Grow and Shrink */
  .grow { flex-grow: 1 }

  /* Misc Flex */
  .flex-wrap { flex-wrap: wrap; }
  .basis-0 { flex-basis: 0; }

  /* Sizing */
  .width-100 { width: 100% }
  .width-75  { width: 75% }
  .width-50  { width: 50% }
  .width-25  { width: 25% }

  .page-width {
    max-width: 40rem;
  }
  .weight-bold {
    font-weight: bold;
  }
  .no-wrap {
    white-space: no-wrap;
  }

  /* Borders */
  .border   { border: 1px solid gray; }
  .border-1 { border: 1px solid gray; }
  .border-2 { border: 2px solid gray; }
  .border-3 { border: 3px solid gray; }

  .border-rad-1-4 { border-radius: 0.25rem; }
  .border-rad-1-2 { border-radius: 0.5rem; }
  .border-rad-1   { border-radius: 1rem; }

  .dev-border {
    border: 1px dashed black;
  }
  .curved-border {
    border-style: solid;
    border-width: 10px;
    border-color: transparent;
    border-radius: 10px
  }

  /* Text */
  .text-white { color: white; }
  .text-black { color: black; }
  .text-gray  { color: gray; }

  .text-left   { text-align: left; }
  .text-center { text-align: center; }
  .text-right  { text-align: right; }
  .underlined  { text-decoration: underline; }

  .is-extra-bold {
    font-weight: 900;
  }
  .is-heavy {
    font-weight: 600;
  }
  textarea:focus {
    outline: none;
  }
</style>

